/**
 * Enhanced ClientsList with Infinite Scroll
 *
 * This example shows how to integrate the lazy loading feature
 * into the existing ClientsList component with filters.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Button, TextField, MenuItem, Box, Typography, Autocomplete } from '@mui/material';
import { Plus, Search as SearchIcon, BrushCleaning, RefreshCcw } from 'lucide-react';
import DataTable from '../common/DataTable/index';
import CommonDialog from '../common/CommonDialog';
import AddClient from './AddClient';
import { fetchClients } from '../../../apis/Clients';
import Tooltip from '../../../utils/Tooltip';
import {
  useMasterId,
  useResponsive,
  isReadonlyMode,
  isDcrScreen,
  userScreenAccess,
  IsAdmins
} from '../../../utils';
import { MastersId, ClientStatusOptions } from '../../../constants/constant';
import {
  FilterFieldSx,
  DropDownSX,
  CSS_PRIMARY,
  PRIMARY_COLOR_HOVER
} from '../../../constants/style';
import { fetchAreaListCombo } from '../../../apis/Masters/Area';
import { fetchCityListCombo } from '../../../apis/Masters/City';
import { fetchUsersCombo } from '../../../apis/Masters/Users';
import Search from '../common/Search';
import ExportExcel from '../common/ExportExcel';
import { fetchDcrClients } from '../../../apis/DCR/Clients';
import { fetchDcrAreas } from '../../../apis/DCR/Area';
import { fetchDcrCityListCombo } from '../../../apis/DCR/City';
import { localStorageKeyName } from '../../../constants/constant';
import { fetchGlobalTypesCombo } from '../../../apis/Masters/GlobalType';

const headers = [
  {
    label: 'Client Code',
    key: 'clientCode',
    maxWidth: 80,
    sortable: true,
    align: 'left',
    resizable: true
  },
  {
    label: 'Client Name',
    key: 'clientName',
    maxWidth: 140,
    sortable: true,
    resizable: true
  },
  {
    label: 'Address 1',
    key: 'address1',
    maxWidth: 140,
    sortable: true,
    resizable: true
  },
  {
    label: 'Address 2',
    key: 'address2',
    maxWidth: 140,
    sortable: true,
    resizable: true
  },
  {
    label: 'Area',
    key: 'area',
    maxWidth: 140,
    sortable: true,
    resizable: true
  },
  {
    label: 'City',
    key: 'city',
    maxWidth: 140,
    sortable: true,
    resizable: true
  },
  {
    label: 'Org Type',
    key: 'orgType',
    maxWidth: 140,
    sortable: true,
    resizable: true
  },
  {
    label: 'In Charge',
    key: 'userName',
    maxWidth: 140,
    sortable: true,
    resizable: true
  },
  {
    label: 'Status',
    key: 'isActive',
    minWidth: 80,
    sortable: true,
    align: 'center',
    render: (val) => {
      const statusText = val ? 'Active' : 'Inactive';
      const badgeClass =
        {
          true: 'bg-success-subtle text-success',
          false: 'bg-danger-subtle text-danger'
        }[val] || 'bg-secondary';
      return <span className={`${badgeClass} px-2 py-1 fw-semibold`}>{statusText}</span>;
    }
  },
  {
    label: 'Actions',
    key: '_actions',
    minWidth: 80,
    sortable: false,
    align: 'center',
    render: (_val, row) => (
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation();
          _editRowHandler?.(row);
        }}
        sx={{
          fontSize: '0.7rem',
          textTransform: 'none',
          borderRadius: 1.5,
          py: 0.3,
          px: 1.2,
          borderColor: 'divider',
          color: 'text.secondary',
          '&:hover': { borderColor: 'primary.main', color: 'primary.main' }
        }}
      >
        Edit
      </Button>
    )
  }
];

const dcrHeaders = [
  {
    label: 'Client Name',
    key: 'dailyCallClientName',
    maxWidth: 160,
    sortable: true,
    resizable: true
  },
  {
    label: 'Address 1',
    key: 'address1',
    maxWidth: 140,
    sortable: true,
    resizable: true
  },
  {
    label: 'Address 2',
    key: 'address2',
    maxWidth: 140,
    sortable: true,
    resizable: true
  },
  {
    label: 'Area',
    key: 'dailyCallArea',
    maxWidth: 140,
    sortable: true,
    resizable: true
  },
  {
    label: 'City',
    key: 'dailyCallCity',
    maxWidth: 140,
    sortable: true,
    resizable: true
  },
  {
    label: 'Org Type',
    key: 'orgType',
    maxWidth: 140,
    sortable: true,
    resizable: true
  },
  {
    label: 'Visiting Time',
    key: 'visitingTime',
    maxWidth: 140,
    sortable: true,
    align: 'left',
    resizable: true
  },
  {
    label: 'Owner Name',
    key: 'contactPersonName1',
    maxWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'Owner No.',
    key: 'contactNo1',
    maxWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'Software Using',
    key: 'softwareName',
    maxWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'Status',
    key: 'isActive',
    maxWidth: 140,
    sortable: true,
    align: 'center',
    render: (val) => {
      const statusText = val ? 'Active' : 'Inactive';
      const badgeClass =
        {
          true: 'bg-success-subtle text-success',
          false: 'bg-danger-subtle text-danger'
        }[val] || 'bg-secondary';
      return <span className={`${badgeClass} px-2 py-1 fw-semibold`}>{statusText}</span>;
    }
  },
  {
    label: 'Actions',
    key: '_actions',
    maxWidth: 80,
    sortable: false,
    align: 'center',
    render: (_val, row) => (
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation();
          _editRowHandler?.(row);
        }}
        sx={{
          fontSize: '0.7rem',
          textTransform: 'none',
          borderRadius: 1.5,
          py: 0.3,
          px: 1.2,
          borderColor: 'divider',
          color: 'text.secondary',
          '&:hover': { borderColor: 'primary.main', color: 'primary.main' }
        }}
      >
        Edit
      </Button>
    )
  }
];

const filtersConfig = {
  clientName: '',
  areaGUID: null,
  cityGUID: null,
  userGUID: null,
  orgTypeGUID: null,
  statusGUID: 1,
  visitingTime: ''
};

let _editRowHandler = null;

const ClientListWithLazyLoading = () => {
  // Filters state (for UI inputs)
  const [filters, setFilters] = useState(filtersConfig);

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // Filter options
  const [inChargeOptions, setInChargeOptions] = useState([]);

  const [clients, setClients] = useState([]);
  const [areas, setAreas] = useState([]);
  const [cities, setCities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { isMobile } = useResponsive();
  const [orgTypes, setOrgTypes] = useState([]);

  const user = localStorage.getItem(localStorageKeyName);
  const userDetails = user ? JSON.parse(user) : null;

  const isDcrClient = isDcrScreen();
  const tableHeaders = isDcrClient ? dcrHeaders : headers;

  const isAdmin = IsAdmins();

  const userAccess = userScreenAccess();

  // Wire module-level handler for the Actions column
  _editRowHandler = (row) => {
    setSelectedClient(row);
    setOpenDialog(true);
  };

  const handleAddClient = () => {
    if (!userAccess.hasAddPermission) return;
    setSelectedClient(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    fetchAreaListResponse();
    setOpenDialog(false);
    setSelectedClient(null);
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleFetch();
    }
  };

  const handleOrgTypeChange = (event, value) => {
    const orgTypeGUID = value?.globalTypeID || 0;
    const next = { ...filters, orgTypeGUID };
    setFilters(next);
  };

  const fetchAreaListResponse = async () => {
    if (isDcrClient) {
      const dcrResponse = await fetchDcrAreas(2);
      setAreas(dcrResponse || []);
    } else {
      const response = await fetchAreaListCombo();
      setAreas(response || []);
    }
  };

  const fetchDependencies = async () => {
    try {
      const [usersRes, areasRes, citiesRes, orgTypes] = await Promise.all([
        fetchUsersCombo(),
        fetchAreaListCombo(),
        fetchCityListCombo(),
        fetchGlobalTypesCombo(MastersId.ORGANIZATION_TYPE)
      ]);

      const loadedUsers = usersRes || [];
      const loadedAreas = areasRes || [];
      const loadedCities = citiesRes || [];
      const loadedOrgType = orgTypes || [];

      setInChargeOptions(loadedUsers);
      setAreas(loadedAreas);
      setCities(loadedCities);
      setOrgTypes(loadedOrgType);
    } catch (err) {
      console.log('Failed to fetch dependencies:', err);
    }
  };

  const fetchDcrDependencies = async () => {
    try {
      const [areasRes, citiesRes, orgTypes] = await Promise.all([
        fetchDcrAreas(2),
        fetchDcrCityListCombo(),
        fetchGlobalTypesCombo(MastersId.ORGANIZATION_TYPE)
      ]);

      const loadedAreas = areasRes || [];
      const loadedCities = citiesRes || [];
      const loadedOrgType = orgTypes || [];

      setAreas(loadedAreas);
      setCities(loadedCities);
      setOrgTypes(loadedOrgType);
    } catch (err) {
      console.log('Failed to fetch dependencies:', err);
    }
  };

  // Fetch dependencies on mount
  React.useEffect(() => {
    if (isDcrClient) {
      fetchDcrDependencies();
    } else {
      fetchDependencies();
    }
  }, [isDcrClient]);

  /**
   * NEW: Reset infinite scroll when filters change
   * This forces the DataTable to reinitialize with new filters
   */
  const handleResetFilters = () => {
    const payload = { ...filtersConfig, userGUID: !isDcrClient ? userDetails.userGUID : 0 };
    setFilters(payload);
    fetchClientListResponse(payload);
    // Note: We don't reset apiFilters or trigger fetch here
  };

  const handleFetch = () => {
    fetchClientListResponse(filters);
  };

  const fetchClientListResponse = async (filters) => {
    setIsLoading(true);
    try {
      const payload = filters;
      const dcrPayload = {
        ...filters,
        dailyCallCityGUID: filters?.cityGUID || 0,
        dailyCallAreaGUID: filters?.areaGUID || 0,
        dailyCallClientName: filters?.clientName || ''
      };
      const response = isDcrClient
        ? await fetchDcrClients(dcrPayload)
        : await fetchClients(payload);
      setClients(response || []);
    } catch (err) {
      console.log('Error fetching client list:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const payload = { ...filtersConfig, userGUID: !isDcrClient ? userDetails.userGUID : 0 };
    setFilters(payload);
    fetchClientListResponse(payload);
  }, [isDcrClient]);

  // ── Autocomplete change handler ─────────────────────────────────────────────
  const handleCityChange = (event, value) => {
    const cityGUID = value?.cityGUID || value?.dailyCallCityGUID || 0;
    const next = { ...filters, cityGUID };
    setFilters(next);
  };

  const handleAreaChange = (event, value) => {
    const areaGUID = value?.areaGUID || value?.dailyCallAreaGUID || 0;
    const next = { ...filters, areaGUID };
    setFilters(next);
  };

  const handleInChargeChange = (event, value) => {
    const userGUID = value?.userGUID || 0;
    const next = { ...filters, userGUID };
    setFilters(next);
  };

  const handleStatusChange = (event, value) => {
    const statusGUID = value?.value || 0;
    const next = { ...filters, statusGUID };
    setFilters(next);
  };

  // Filtered data
  const filteredData = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((row) => {
      // Normalize values: convert boolean isActive → display text so
      // searching "Active" or "Inactive" / "InActive" works correctly
      const normalizedValues = Object.entries(row).map(([key, val]) => {
        if (key === 'isActive')
          return typeof val === 'boolean'
            ? val
              ? 'active'
              : 'inactive'
            : String(val).toLowerCase();
        return String(val).toLowerCase();
      });
      return normalizedValues.some((v) => v.includes(q));
    });
  }, [searchQuery, clients]);

  return (
    <div className="w-100">
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
          justifyContent: { md: 'space-between', xs: 'item-flex' },
          mb: 3
        }}
      >
        <Typography variant="h6" fontWeight="bold" color="text.primary">
          Client List
        </Typography>
      </Box>
      <Box
        sx={{
          mb: 2,
          p: 2,
          backgroundColor: '#fff',
          borderRadius: 2,
          boxShadow: '0px 2px 10px rgba(0,0,0,0.04)',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 1.5,
            width: '100%',
            alignItems: 'center'
          }}
        >
          {/* Client Name Filter */}
          <TextField
            fullWidth
            size="small"
            label="Client Name"
            name="clientName"
            type="search"
            value={filters.clientName}
            onChange={handleFilterChange}
            sx={{ flex: 1, ...FilterFieldSx }}
            placeholder="Search client..."
            onKeyDown={handleKeyDown}
          />

          <Autocomplete
            size="small"
            fullWidth
            options={areas}
            value={
              areas.find(
                (a) => a.areaGUID === filters.areaGUID || a.dailyCallAreaGUID === filters.areaGUID
              ) || null
            }
            onChange={handleAreaChange}
            disablePortal={false}
            disableClearable={false}
            getOptionLabel={(option) => option?.area || option?.dailyCallArea || ''}
            isOptionEqualToValue={(option, value) =>
              option?.areaGUID === value?.areaGUID ||
              option?.dailyCallAreaGUID === value?.dailyCallAreaGUID
            }
            slotProps={{
              paper: {
                sx: DropDownSX
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Area" placeholder="Select Area" sx={FilterFieldSx} />
            )}
            onKeyDown={handleKeyDown}
          />

          <Autocomplete
            size="small"
            fullWidth
            options={cities}
            value={
              cities.find(
                (c) => c.cityGUID === filters.cityGUID || c.dailyCallCityGUID === filters.cityGUID
              ) || null
            }
            onChange={handleCityChange}
            disablePortal={false}
            disableClearable={false}
            getOptionLabel={(option) => option?.city || option?.dailyCallCity || ''}
            isOptionEqualToValue={(option, value) =>
              option?.cityGUID === value?.cityGUID ||
              option?.dailyCallCityGUID === value?.dailyCallCityGUID
            }
            slotProps={{
              paper: {
                sx: DropDownSX
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="City" placeholder="Select City" sx={FilterFieldSx} />
            )}
            onKeyDown={handleKeyDown}
          />

          {!isDcrClient && (
            <Autocomplete
              size="small"
              fullWidth
              options={inChargeOptions}
              value={inChargeOptions.find((u) => u.userGUID === filters.userGUID) || null}
              onChange={handleInChargeChange}
              disablePortal={false}
              disableClearable={false}
              getOptionLabel={(option) => option?.userName || ''}
              isOptionEqualToValue={(option, value) => option?.userGUID === value?.userGUID}
              readOnly={!isAdmin}
              onKeyDown={handleKeyDown}
              slotProps={{
                paper: {
                  sx: DropDownSX
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="In Charge"
                  placeholder="Select In Charge"
                  sx={FilterFieldSx}
                />
              )}
            />
          )}

          <Autocomplete
            size="small"
            fullWidth
            options={orgTypes}
            value={orgTypes.find((o) => o.globalTypeID === filters.orgTypeGUID) || null}
            onChange={handleOrgTypeChange}
            disablePortal={false}
            getOptionLabel={(option) => option?.globalTypeName || ''}
            isOptionEqualToValue={(option, value) => option?.globalTypeID === value?.globalTypeID}
            onKeyDown={handleKeyDown}
            slotProps={{
              paper: {
                sx: DropDownSX
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Organization Type"
                placeholder="Select Organization Type"
                sx={FilterFieldSx}
              />
            )}
          />

          <Autocomplete
            size="small"
            fullWidth
            options={ClientStatusOptions}
            value={ClientStatusOptions.find((u) => u.value === filters.statusGUID) || null}
            onChange={handleStatusChange}
            disablePortal={false}
            disableClearable={false}
            getOptionLabel={(option) => option?.label || ''}
            isOptionEqualToValue={(option, value) => option?.value === value?.value}
            onKeyDown={handleKeyDown}
            slotProps={{
              paper: {
                sx: DropDownSX
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Status"
                placeholder="Select Status"
                sx={FilterFieldSx}
              />
            )}
          />

          {isDcrClient && (
            <TextField
              fullWidth
              size="small"
              label="Visiting Time"
              name="visitingTime"
              type="search"
              value={filters.visitingTime}
              onChange={handleFilterChange}
              sx={{ flex: 1, ...FilterFieldSx }}
              placeholder="Search visiting time..."
              onKeyDown={handleKeyDown}
            />
          )}

          {/* Buttons Container */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              alignItems: 'center'
            }}
          >
            {/* Fetch Button */}
            <Tooltip text="Fetch">
              <Button
                variant="outlined"
                size="small"
                onClick={handleFetch}
                sx={{
                  textTransform: 'none',
                  borderRadius: 1.5,
                  whiteSpace: 'nowrap',
                  color: CSS_PRIMARY, // text color
                  borderColor: CSS_PRIMARY, // outline color

                  '&:hover': {
                    borderColor: PRIMARY_COLOR_HOVER,
                    color: PRIMARY_COLOR_HOVER
                  }
                }}
              >
                <SearchIcon size={16} />
              </Button>
            </Tooltip>

            {/* Refresh Button */}
            {/* <Tooltip text="Refresh">
              <Button
                variant="outlined"
                size="small"
                onClick={handleFetch}
                sx={{
                  textTransform: 'none',
                  borderRadius: 1.5,
                  whiteSpace: 'nowrap'
                }}
              >
                <RefreshCcw size={16} />
              </Button>
            </Tooltip> */}

            {/* Reset Button */}
            <Tooltip text="Reset filters">
              <Button
                variant="outlined"
                size="small"
                onClick={handleResetFilters}
                sx={{
                  textTransform: 'none',
                  borderRadius: 1.5,
                  whiteSpace: 'nowrap',
                  width: '32px',
                  color: CSS_PRIMARY, // text color
                  borderColor: CSS_PRIMARY, // outline color

                  '&:hover': {
                    borderColor: PRIMARY_COLOR_HOVER,
                    color: PRIMARY_COLOR_HOVER
                  }
                }}
              >
                <BrushCleaning size={16} />
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* ── Toolbar ───────────────────────────────────────────────────── */}
      <div className="d-flex align-items-center justify-content-between mb-2 gap-2 flex-wrap">
        <Search
          width={isMobile ? '170px' : '280px'}
          height="36px"
          placeholder="Search by client name, area, city or in charge..."
          debounce={250}
          onSearch={setSearchQuery}
          onClear={() => setSearchQuery('')}
        />

        <div className="d-flex align-items-center gap-2">
          <ExportExcel
            data={clients}
            fileName={`${isDcrClient ? 'DCR-Clients' : 'Clients'}_${new Date()
              .toISOString()
              .slice(0, 10)}.xlsx`}
            sheetName={isDcrClient ? 'DCR Clients' : 'Clients'}
            buttonLabel="Excel"
            exportKeys={tableHeaders.filter((h) => h.key !== '_actions').map((h) => h.key)}
            exportHeaders={tableHeaders.filter((h) => h.key !== '_actions').map((h) => h.label)}
          />

          <Button
            variant="contained"
            size="small"
            startIcon={<Plus size={15} />}
            onClick={handleAddClient}
            sx={{
              background: 'var(--primary, #82CAFF)',
              '&:hover': { background: 'var(--primary-dark, #27ae60)' },
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 2,
              py: 0.8,
              fontSize: '0.83rem',
              boxShadow: '0 4px 12px rgba(46,204,113,0.28)',
              display: userAccess.hasAddPermission ? 'inline-flex' : 'none'
            }}
          >
            Add
          </Button>
        </div>
      </div>

      <DataTable
        headers={tableHeaders}
        data={filteredData}
        isLoading={isLoading}
        selectable={false}
        emptyMessage={'No clients found in the system.'}
        enablePagination={true}
        zebra={false}
        onRowClick={_editRowHandler}
      />

      <CommonDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title={selectedClient ? 'Edit Client' : 'Add New Client'}
        persistent={true}
        fullScreen={isMobile ? true : false}
        maxWidth="xl"
      >
        <AddClient
          isDcrClient={isDcrClient}
          client={selectedClient}
          inChargeOptions={inChargeOptions}
          areasOptions={areas}
          citiesOptions={cities}
          userAccess={userAccess}
          onSuccess={() => {
            handleCloseDialog();
            handleFetch();
          }}
          onCancel={handleCloseDialog}
        />
      </CommonDialog>
    </div>
  );
};

export default ClientListWithLazyLoading;
